import { clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Matches any class part value — used as a catch-all validator for CDS token names.
 * tailwind-merge calls this after matching the key prefix (e.g. `cds-bg`),
 * so returning true means "any token name after the prefix is valid".
 */
var isCdsValue = function isCdsValue() {
  return true;
};

/**
 * Excludes directional suffixes (x-, y-, t-, r-, b-, l-) so that
 * e.g. `cds-p-5` matches the `cds-p` group but `cds-px-5`
 * does NOT (it should match `cds-px` instead).
 */
var isNonDirectional = function isNonDirectional(v) {
  return !/^[xytrlb]-/.test(v);
};

/**
 * For deprecated `cds-border-*`: border color tokens use semantic names (e.g. `default`, `brand`),
 * while border width tokens use numeric keys (e.g. `16`, `1`).
 * This validator matches only color tokens by excluding numeric and directional values.
 */
var isBorderColorToken = function isBorderColorToken(v) {
  return !/^[xytrlb]-/.test(v) && !/^\d/.test(v) && !v.startsWith('spacing-');
};
var customTwMerge = /*#__PURE__*/extendTailwindMerge({
  prefix: 'tw-',
  classGroups: {
    // --- Semantic color tokens ---
    'cds-bg': [{
      'cds-bg': [isCdsValue]
    }],
    'cds-text': [{
      'cds-text': [isCdsValue]
    }],
    // Matches both deprecated tw-cds-border-default and canonical tw-cds-border-color-default
    'cds-border-color': [{
      'cds-border': [isBorderColorToken]
    }, {
      'cds-border-color': [isCdsValue]
    }],
    // --- Side-specific border color tokens ---
    'cds-border-t-color': [{
      'cds-border-t': [isCdsValue]
    }],
    'cds-border-r-color': [{
      'cds-border-r': [isCdsValue]
    }],
    'cds-border-b-color': [{
      'cds-border-b': [isCdsValue]
    }],
    'cds-border-l-color': [{
      'cds-border-l': [isCdsValue]
    }],
    'cds-border-x-color': [{
      'cds-border-x': [isCdsValue]
    }],
    'cds-border-y-color': [{
      'cds-border-y': [isCdsValue]
    }],
    // --- Border radius ---
    // Matches both deprecated tw-cds-radius-s and canonical tw-cds-rounded-s
    'cds-radius': [{
      'cds-radius': [isCdsValue]
    }, {
      'cds-rounded': [isCdsValue]
    }],
    // --- Semantic named padding ---
    // tw-cds-padding-card-s, tw-cds-padding-card-m, etc. → padding
    'cds-padding': [{
      'cds-padding': [isCdsValue]
    }],
    // --- Spacing: margin ---
    'cds-m': [{
      'cds-m': [isNonDirectional]
    }],
    'cds-mx': [{
      'cds-mx': [isCdsValue]
    }],
    'cds-my': [{
      'cds-my': [isCdsValue]
    }],
    'cds-mt': [{
      'cds-mt': [isCdsValue]
    }],
    'cds-mr': [{
      'cds-mr': [isCdsValue]
    }],
    'cds-mb': [{
      'cds-mb': [isCdsValue]
    }],
    'cds-ml': [{
      'cds-ml': [isCdsValue]
    }],
    // --- Spacing: padding ---
    'cds-p': [{
      'cds-p': [isNonDirectional]
    }],
    'cds-px': [{
      'cds-px': [isCdsValue]
    }],
    'cds-py': [{
      'cds-py': [isCdsValue]
    }],
    'cds-pt': [{
      'cds-pt': [isCdsValue]
    }],
    'cds-pr': [{
      'cds-pr': [isCdsValue]
    }],
    'cds-pb': [{
      'cds-pb': [isCdsValue]
    }],
    'cds-pl': [{
      'cds-pl': [isCdsValue]
    }],
    // --- Spacing: gap ---
    'cds-gap': [{
      'cds-gap': [isNonDirectional]
    }],
    'cds-gap-x': [{
      'cds-gap-x': [isCdsValue]
    }],
    'cds-gap-y': [{
      'cds-gap-y': [isCdsValue]
    }]
  },
  conflictingClassGroups: {
    // CDS bg ↔ standard bg-color
    'cds-bg': ['bg-color'],
    'bg-color': ['cds-bg'],
    // CDS text ↔ standard text-color
    'cds-text': ['text-color'],
    'text-color': ['cds-text'],
    // CDS border-color ↔ standard border-color
    'cds-border-color': ['border-color', 'cds-border-t-color', 'cds-border-r-color', 'cds-border-b-color', 'cds-border-l-color', 'cds-border-x-color', 'cds-border-y-color'],
    'border-color': ['cds-border-color', 'cds-border-t-color', 'cds-border-r-color', 'cds-border-b-color', 'cds-border-l-color', 'cds-border-x-color', 'cds-border-y-color'],
    // CDS side-specific border color ↔ standard side-specific border color
    // NOTE: tailwind-merge v2 uses "border-color-{side}" naming (not "border-{side}-color")
    'cds-border-t-color': ['border-color-t'],
    'border-color-t': ['cds-border-t-color'],
    'cds-border-r-color': ['border-color-r'],
    'border-color-r': ['cds-border-r-color'],
    'cds-border-b-color': ['border-color-b'],
    'border-color-b': ['cds-border-b-color'],
    'cds-border-l-color': ['border-color-l'],
    'border-color-l': ['cds-border-l-color'],
    'cds-border-x-color': ['border-color-x', 'border-color-r', 'border-color-l'],
    'border-color-x': ['cds-border-x-color'],
    'cds-border-y-color': ['border-color-y', 'border-color-t', 'border-color-b'],
    'border-color-y': ['cds-border-y-color'],
    // CDS radius/rounded ↔ standard rounded
    'cds-radius': ['rounded'],
    rounded: ['cds-radius'],
    // CDS named padding (padding-card-*) overrides all standard + CDS padding
    'cds-padding': ['p', 'px', 'py', 'pt', 'pr', 'pb', 'pl', 'cds-p', 'cds-px', 'cds-py', 'cds-pt', 'cds-pr', 'cds-pb', 'cds-pl'],
    // CDS margin ↔ standard margin
    'cds-m': ['m', 'mx', 'my', 'mt', 'mr', 'mb', 'ml'],
    m: ['cds-m', 'cds-mx', 'cds-my', 'cds-mt', 'cds-mr', 'cds-mb', 'cds-ml'],
    'cds-mx': ['mx', 'mr', 'ml'],
    mx: ['cds-mx'],
    'cds-my': ['my', 'mt', 'mb'],
    my: ['cds-my'],
    'cds-mt': ['mt'],
    mt: ['cds-mt'],
    'cds-mr': ['mr'],
    mr: ['cds-mr'],
    'cds-mb': ['mb'],
    mb: ['cds-mb'],
    'cds-ml': ['ml'],
    ml: ['cds-ml'],
    // CDS padding ↔ standard padding
    'cds-p': ['p', 'px', 'py', 'pt', 'pr', 'pb', 'pl'],
    p: ['cds-p', 'cds-px', 'cds-py', 'cds-pt', 'cds-pr', 'cds-pb', 'cds-pl', 'cds-padding'],
    'cds-px': ['px', 'pr', 'pl'],
    px: ['cds-px'],
    'cds-py': ['py', 'pt', 'pb'],
    py: ['cds-py'],
    'cds-pt': ['pt'],
    pt: ['cds-pt'],
    'cds-pr': ['pr'],
    pr: ['cds-pr'],
    'cds-pb': ['pb'],
    pb: ['cds-pb'],
    'cds-pl': ['pl'],
    pl: ['cds-pl'],
    // CDS gap ↔ standard gap
    'cds-gap': ['gap', 'gap-x', 'gap-y'],
    gap: ['cds-gap'],
    'cds-gap-x': ['gap-x'],
    'gap-x': ['cds-gap-x'],
    'cds-gap-y': ['gap-y'],
    'gap-y': ['cds-gap-y']
  }
});
export function cn() {
  for (var _len = arguments.length, inputs = new Array(_len), _key = 0; _key < _len; _key++) {
    inputs[_key] = arguments[_key];
  }
  return customTwMerge(clsx(inputs));
}
export function getFromNestedObject(obj, path) {
  var index = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  var key = path[index];

  // biome-ignore lint/style/useBlockStatements: single-line early return
  if (!key) return '';
  var result = obj[key];

  // biome-ignore lint/style/useBlockStatements: single-line early return
  if (!result) return '';

  // biome-ignore lint/style/useBlockStatements: single-line early return
  if (typeof result === 'string') return result;
  return getFromNestedObject(Object(result), path, index + 1);
}

/**
 * No-op tagged template literal used as a Tailwind CSS class marker.
 *
 * This function has no runtime effect — it returns the string unchanged.
 * Its sole purpose is to signal to the Tailwind CSS IntelliSense VS Code
 * extension (`bradlc.vscode-tailwindcss`) that the contents of the template
 * literal are Tailwind class names, enabling autocomplete, hover previews,
 * and linting inside string constants.
 *
 * **Setup:** The extension must be configured to recognise `tw` as a class
 * function in `.vscode/settings.json`:
 * ```json
 * { "tailwindCSS.classFunctions": ["tw"] }
 * ```
 *
 * **Usage:**
 * ```ts
 * const STICKY_COL = tw`left-0 w-60 min-w-[15rem]`;
 * const HEADER = tw`h-12 px-4 text-left border-b bg-subtle`;
 *
 * // Interpolation is supported but IntelliSense won't resolve it:
 * const HEADER_TODAY = tw`${HEADER} bg-layer-raised text-inverse`;
 * ```
 *
 * **Do not** use this for dynamic class composition at runtime — use a
 * dedicated utility like `clsx` or `cx` for that instead.
 *
 * @see https://marketplace.visualstudio.com/items?itemName=bradlc.vscode-tailwindcss
 */
export var tw = String.raw;